import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { collection, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { CheckCircle, Calendar, Crown } from 'lucide-react';

const stripePromise = loadStripe(
    'pk_test_51T4qoZQ8au9doSF20VcYScR1WHmi46fdjNNCazZ1oUxl83MXx50vlMYeN4LbWfokCnJq7DuGqZJ558DShS51UdLJ00DH3uCl6e'
);

const PricingPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [loadingPlan, setLoadingPlan] = useState(null); // 'standard', 'premium', sau null
    const [error, setError] = useState('');
    const [userSubscription, setUserSubscription] = useState(null);
    const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);

    // Citește subscription-ul user-ului din Firestore
    useEffect(() => {
        const loadSubscription = async () => {
            if (!currentUser) return;

            try {
                // Citește din userResponses
                const userResponsesRef = doc(db, 'userResponses', currentUser.uid);
                const userResponsesSnap = await getDoc(userResponsesRef);

                if (userResponsesSnap.exists()) {
                    const data = userResponsesSnap.data();
                    setUserSubscription(data.subscription || 'none');
                }

                // Citește data de expirare din subscriptions
                const subscriptionsRef = collection(db, 'customers', currentUser.uid, 'subscriptions');
                const unsubscribe = onSnapshot(subscriptionsRef, (snapshot) => {
                    snapshot.forEach((doc) => {
                        const subData = doc.data();
                        if (subData.status === 'active' || subData.status === 'trialing') {
                            // current_period_end e timestamp Firestore
                            const endDate = subData.current_period_end?.toDate();
                            setSubscriptionEndDate(endDate);
                        }
                    });
                });

                return () => unsubscribe();
            } catch (error) {
                console.error('Eroare la încărcarea subscription:', error);
            }
        };

        loadSubscription();
    }, [currentUser]);

    const handleCheckout = async (priceId, planType) => {
        if (!currentUser) {
            navigate('/login');
            return;
        }

        setLoadingPlan(planType); // 'standard' sau 'premium'
        setError('');

        try {
            console.log('📦 Creez sesiune checkout pentru priceId:', priceId);

            // Creează documentul în colecția checkout_sessions
            const checkoutSessionRef = collection(
                db,
                'customers',
                currentUser.uid,
                'checkout_sessions'
            );

            const docRef = await addDoc(checkoutSessionRef, {
                price: priceId,
                success_url: window.location.origin + '/payment-success',
                cancel_url: window.location.origin + '/pricing',
            });

            console.log('✅ Document checkout session creat:', docRef.id);

            // Ascultă pentru modificări ale documentului
            const unsubscribe = onSnapshot(docRef, async (snap) => {
                const data = snap.data();
                console.log('📡 Snapshot primit:', data);

                if (data?.url) {
                    console.log('✅ URL Stripe primit:', data.url);

                    // Redirecționează direct la URL-ul Stripe Checkout
                    window.location.href = data.url;
                    unsubscribe();
                }

                if (data?.error) {
                    console.error('❌ Eroare în document:', data.error);
                    setError(data.error.message || 'A apărut o eroare la inițierea plății.');
                    setLoadingPlan(null);
                    unsubscribe();
                }
            });

        } catch (err) {
            console.error('❌ Eroare detaliată:', err);
            setError('A apărut o eroare la inițierea plății: ' + err.message);
            setLoadingPlan(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('ro-RO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderSubscriptionButton = (planType, priceId) => {
        const isCurrentPlan = userSubscription === planType;
        const hasHigherPlan = planType === 'standard' && userSubscription === 'premium';
        const isLoading = loadingPlan === planType;
        const isAnyLoading = loadingPlan !== null;

        // Dacă user-ul are acest plan sau un plan mai mare
        if (isCurrentPlan || hasHigherPlan) {
            return (
                <div className="w-full bg-green-50 border-2 border-green-500 text-green-700 font-semibold py-3 px-4 rounded-lg flex items-center justify-center">
                    <CheckCircle size={20} className="mr-2" />
                    {isCurrentPlan ? 'Plan Activ' : 'Plan Inclus'}
                </div>
            );
        }

        // Buton normal de cumpărare
        return (
            <button
                onClick={() => handleCheckout(priceId, planType)}
                disabled={isAnyLoading}
                className={`w-full font-semibold py-3 px-4 rounded-lg transition ${planType === 'premium'
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                {isLoading ? 'Se procesează...' : `Alege ${planType === 'premium' ? 'Premium' : 'Standard'}`}
            </button>
        );
    };

    const renderExpirationInfo = (planType) => {
        const isCurrentPlan = userSubscription === planType;
        const hasHigherPlan = planType === 'standard' && userSubscription === 'premium';

        if ((isCurrentPlan || hasHigherPlan) && subscriptionEndDate) {
            return (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
                    <Calendar size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                        <div className="font-medium text-blue-900">
                            {isCurrentPlan ? 'Abonamentul tău expiră la:' : 'Inclus în planul Premium:'}
                        </div>
                        <div className="text-blue-700">
                            {formatDate(subscriptionEndDate)}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-slate-900 mb-4">
                        Alege planul potrivit pentru tine
                    </h1>
                    <p className="text-xl text-slate-600">
                        Acces complet la evaluările psihologice și analize personalizate
                    </p>

                    {userSubscription && userSubscription !== 'none' && (
                        <div className="mt-6 inline-flex items-center gap-2 bg-green-50 border-2 border-green-500 rounded-full px-6 py-3">
                            <Crown className="text-green-600" size={20} />
                            <span className="text-green-700 font-semibold">
                                Plan Activ: {userSubscription === 'standard' ? 'Standard' : 'Premium'}
                            </span>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="max-w-md mx-auto mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
                        {error}
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {/* Standard */}
                    <div className={`bg-white rounded-2xl shadow-lg border-2 p-8 flex flex-col ${userSubscription === 'standard' ? 'border-green-500' : 'border-slate-200'
                        }`}>
                        {userSubscription === 'standard' && (
                            <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold flex items-center">
                                <CheckCircle size={16} className="mr-1" />
                                Activ
                            </div>
                        )}
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Standard</h2>
                        <p className="text-slate-600 mb-6">Pentru începutul călătoriei tale</p>
                        <div className="text-4xl font-bold text-blue-600 mb-6">
                            29€ <span className="text-lg text-slate-500">/ lună</span>
                        </div>
                        <ul className="space-y-3 mb-8 flex-1">
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Teste Nivel 1 și 2</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Profil relațional de bază</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Salvare progres în cloud</span>
                            </li>
                        </ul>
                        {renderSubscriptionButton('standard', 'price_1T5wbFQ8au9doSF238JjdALM')}
                        {renderExpirationInfo('standard')}
                    </div>

                    {/* Premium */}
                    <div className={`bg-white rounded-2xl shadow-lg border-2 p-8 flex flex-col relative ${userSubscription === 'premium' ? 'border-green-500' : 'border-purple-500'
                        }`}>
                        {userSubscription === 'premium' ? (
                            <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold flex items-center">
                                <CheckCircle size={16} className="mr-1" />
                                Activ
                            </div>
                        ) : (
                            <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                                Popular
                            </div>
                        )}
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Premium</h2>
                        <p className="text-slate-600 mb-6">Acces complet la toate instrumentele</p>
                        <div className="text-4xl font-bold text-purple-600 mb-6">
                            59€ <span className="text-lg text-slate-500">/ lună</span>
                        </div>
                        <ul className="space-y-3 mb-8 flex-1">
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Toate testele (Nivel 1-4)</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Profil psihologic detaliat</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Matching și compatibilitate</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>Recomandări personalizate</span>
                            </li>
                        </ul>
                        {renderSubscriptionButton('premium', 'price_1T5wetQ8au9doSF2aB00yZhv')}
                        {renderExpirationInfo('premium')}
                    </div>
                </div>

                <p className="text-center text-sm text-slate-500 mt-8">
                    Plăți securizate prin Stripe. Poți anula oricând.
                </p>
            </div>
        </div>
    );
};

export default PricingPage;